/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { DxpServiceToken, checkTokenPermission } from '@squiz/dxp-auth';
import { useConsoleServiceStore } from 'dxpConsoleCore-mf/State';
import { DXP_CONSOLE_SQUIZ_ROOT_REFERENCE_ID } from '../../Constants/App.constants';
import { EntityDxpUserMe, EntityDxpUserMeBaseTenant, EntityDxpUserMeOrganisation } from '../../Services/console';
import { getFlatTenantList } from '../GetFlatTenantList/GetFlatTenantList';

/**
 * Determine if the current user has permission to the requested resource.
 * @param {string} permissionToCheck The permission to check like `dxpConsole.TENANT_READ`.
 */
export const hasPermission = (permissionToCheck: string): boolean => {
  const { tokens }: { tokens: Array<DxpServiceToken> | null } = useConsoleServiceStore.getState();
  const tenantReferenceId = getCurrentTenantReferenceId();

  if (!tenantReferenceId || !tokens) {
    return false;
  }

  // Get the DXP service token for the tenant as well as the root tenant.
  const tenantToken = tokens.find((token) => token.tenant === tenantReferenceId);
  const rootToken = tokens.find((token) => token.tenant === DXP_CONSOLE_SQUIZ_ROOT_REFERENCE_ID);

  if (!tenantToken && !rootToken) {
    return false;
  }

  let hasTenantPermission = false;

  // Perform permission check against the current tenant
  if (tenantToken) {
    hasTenantPermission = checkTokenPermission(tenantToken, permissionToCheck);
  }

  // If no permission as a direct member, try with root token
  if (!hasTenantPermission && rootToken) {
    return checkTokenPermission(rootToken, permissionToCheck);
  }

  return hasTenantPermission;
};

/**
 * Enable permission checking to work outside of a tenant.
 */
const getCurrentTenantReferenceId = (): string => {
  const {
    consoleUser,
    tenant,
  }: {
    consoleUser: EntityDxpUserMe | null;
    tenant: EntityDxpUserMeOrganisation;
  } = useConsoleServiceStore.getState();

  if (tenant) {
    return tenant.tenantReferenceId;
  } else if (consoleUser && consoleUser.tenantList.length) {
    const tenantList = consoleUser.tenantList;

    // Get tenants the user has access to. Typecast to allow filter to work.
    const allTenants = getFlatTenantList(tenantList) as Array<EntityDxpUserMeBaseTenant>;
    allTenants.filter((tenant) => Boolean(tenant.tenantReferenceId));

    if (allTenants.length) {
      return allTenants[0].tenantReferenceId;
    }
  }

  return '';
};
